import React, {useEffect, useState} from 'react';
import {useLocation} from "react-router-dom";
import { useForm } from 'react-hook-form';
import ReactModal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useSystemMessage } from '../../../hooks/useSystemMessage';
import { tasklistApi } from '../../../apis/tasklist/tasklistApi';
import icon05 from '../../../assets/images/ROOM_TaskFile_Re_05.png';
import { ListList } from '../../../types/types';
import useLoading from '../../../hooks/useLoading';
import {ApiClientError} from "../../../apis/errors";
import {ValidationErrorMessages} from "../common/ValidationErrorMessages";
import {logError} from "../../../libs/utils/logError";
import useAppRouter from "../../../hooks/useAppRouter";

interface UpdateListModalProps {
  listList: ListList;
  onSubmitted: () => Promise<void>;
}

interface FormValues {
  name: string;
}

export const UpdateListModal = (props: UpdateListModalProps) => {
  const { listList, onSubmitted } = props;
  const withLoading = useLoading();
  const location = useLocation();
  const { goto } = useAppRouter();
  const { success } = useSystemMessage();
  const [listName, setListName] = React.useState<string>(listList.name);

  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);

  useEffect(() => {
    const fetch = async () => {
      withLoading(async () => {
        try {
          const response = await tasklistApi.show(listList.id, {});
          setListName(response.list.name);
        } catch(e) {
          await logError(
            location.pathname,
            'タスクリストの取得に失敗しました。',
            e
          );

          goto('internalServerError');
        }
      });
    };
    fetch();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [listList]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: listName,
    },
    reValidateMode: 'onSubmit',
  });

  const onSubmit = async (data: FormValues) => {
    setApiClientError(null);
    try {
      await tasklistApi.update(listList.id, { listName: data.name }, {});
      await onSubmitted();
      await success('リスト名を更新しました。');
    } catch(e) {
      await logError(
        location.pathname,
        'タスクリスト名の更新に失敗しました。',
        e
      );

      if (e instanceof ApiClientError) {
        setApiClientError(e);
        return;
      }

      goto('internalServerError');
    }
  };

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => onSubmitted()}
        className="content"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">リストの編集</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => onSubmitted()}
            >
              <img src={icon05} alt="icon" />
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-modal-content">
              <div className="text-danger text-xs mb-4">
                { apiClientError?.message }
                { (Object.keys(errors).length > 0) && '入力エラーがあります' }
              </div>
              <input
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...register('name', { required: true, maxLength: 100 })}
                placeholder="リスト名"
                className="text-field"
              />
              {errors.name && errors.name.type === 'required' && (
                <span className="my-2 text-red-500 text-xs">
                  リスト名は必須です
                </span>
              )}
              {errors.name && errors.name.type === 'maxLength' && (
                <span className="my-2 text-red-500 text-xs">
                  リスト名は100文字以下で指定してください
                </span>
              )}
              <ValidationErrorMessages
                {...{
                  fieldErrors: apiClientError?.errors,
                  field: 'listName',
                }}
              />
            </div>
            <div className="form-modal-footer-container">
              <div className="form-modal-footer">
                <button
                  className="btn-sub"
                  type="button"
                  onClick={() => onSubmitted()}
                >
                  キャンセル
                </button>
                <button
                  className="btn-primary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin mr-4"
                      />
                      更 新
                    </>
                  ) : (
                    '更　新'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </ReactModal>
    </>
  );
};
